/**
 * Local cache service using IndexedDB for persistent stale-while-revalidate caching.
 * Stores API responses with ETags, scoped per authenticated user.
 */

import { getCacheTTL } from './cacheKeys';

const DB_VERSION = 1;
const STORE_NAME = 'api-cache';

interface CachedEntry<T = unknown> {
  key: string;
  data: T;
  etag: string | null;
  timestamp: number;
}

let currentDb: IDBDatabase | null = null;
let currentUserId: string | null = null;

const getDbName = (userId: string): string => `private-photos-cache-${userId}`;

/**
 * Opens (or reuses) the IndexedDB database for the given user.
 */
const openDb = (userId: string): Promise<IDBDatabase> => {
  if (currentDb && currentUserId === userId) {
    return Promise.resolve(currentDb);
  }

  return new Promise((resolve, reject) => {
    // Close previous DB if switching users
    if (currentDb) {
      currentDb.close();
      currentDb = null;
      currentUserId = null;
    }

    const request = indexedDB.open(getDbName(userId), DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => {
      currentDb = request.result;
      currentUserId = userId;

      // Handle unexpected close (e.g., browser clearing storage)
      currentDb.onclose = () => {
        currentDb = null;
        currentUserId = null;
      };

      resolve(currentDb);
    };

    request.onerror = () => {
      console.warn('IndexedDB open failed, cache will be unavailable', request.error);
      reject(request.error);
    };
  });
};

/**
 * Wraps an IndexedDB transaction request in a Promise.
 */
const wrapRequest = <T>(request: IDBRequest<T>): Promise<T> => {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// ─── Current user ID management ─────────────────────────────

let resolvedUserId: string | null = null;

/**
 * Sets the current user ID for cache scoping.
 * Must be called after authentication.
 */
export const setCacheUserId = (userId: string): void => {
  resolvedUserId = userId;
};

/**
 * Clears the current user ID (e.g., on logout).
 */
export const clearCacheUserId = (): void => {
  resolvedUserId = null;
  if (currentDb) {
    currentDb.close();
    currentDb = null;
    currentUserId = null;
  }
};

const ensureUserId = (): string | null => {
  if (!resolvedUserId) {
    return null;
  }
  return resolvedUserId;
};

// ─── Public API ─────────────────────────────────────────────

/**
 * Gets a cached entry by key. Returns null if not found or expired.
 */
export const cacheGet = async <T>(key: string): Promise<CachedEntry<T> | null> => {
  try {
    const userId = ensureUserId();
    if (!userId) return null;
    const db = await openDb(userId);
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const entry = await wrapRequest(store.get(key)) as CachedEntry<T> | undefined;

    if (!entry) return null;

    // Check hard expiry using per-key TTL
    if (Date.now() - entry.timestamp > getCacheTTL(key)) {
      // Expired — delete in background, don't await
      cacheInvalidate(key).catch(() => {});
      return null;
    }

    return entry;
  } catch {
    // IndexedDB unavailable — graceful degradation
    return null;
  }
};

/**
 * Stores a cache entry.
 */
export const cacheSet = async <T>(key: string, data: T, etag: string | null): Promise<void> => {
  try {
    const userId = ensureUserId();
    if (!userId) return;
    const db = await openDb(userId);
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const entry: CachedEntry<T> = { key, data, etag, timestamp: Date.now() };
    await wrapRequest(store.put(entry));
  } catch {
    // IndexedDB unavailable — silently ignore
  }
};

/**
 * Invalidates (deletes) a single cache entry.
 */
export const cacheInvalidate = async (key: string): Promise<void> => {
  try {
    const userId = ensureUserId();
    if (!userId) return;
    const db = await openDb(userId);
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await wrapRequest(store.delete(key));
  } catch {
    // IndexedDB unavailable — silently ignore
  }
};

/**
 * Invalidates all cache entries whose keys start with the given prefix.
 */
export const cacheInvalidateByPrefix = async (prefix: string): Promise<void> => {
  try {
    const userId = ensureUserId();
    if (!userId) return;
    const db = await openDb(userId);
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    // Use cursor to find matching keys
    const request = store.openCursor();
    await new Promise<void>((resolve, reject) => {
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve();
          return;
        }
        const key = cursor.key as string;
        if (key === prefix || key.startsWith(`${prefix}:`)) {
          cursor.delete();
        }
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
    });
  } catch {
    // IndexedDB unavailable — silently ignore
  }
};

/**
 * Clears all cached data for the current user.
 */
export const cacheClear = async (): Promise<void> => {
  try {
    const userId = ensureUserId();
    if (!userId) return;
    const db = await openDb(userId);
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await wrapRequest(store.clear());
  } catch {
    // IndexedDB unavailable — silently ignore
  }
};

/**
 * Deletes the entire IndexedDB database for the current user.
 * Used on logout for complete cleanup.
 */
export const cacheDeleteDatabase = async (): Promise<void> => {
  try {
    const userId = resolvedUserId;
    if (!userId) return;

    if (currentDb) {
      currentDb.close();
      currentDb = null;
      currentUserId = null;
    }

    await new Promise<void>((resolve, reject) => {
      const request = indexedDB.deleteDatabase(getDbName(userId));
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
      request.onblocked = () => resolve(); // Best effort
    });
  } catch {
    // Best effort cleanup
  }
};
